import type { AxiosResponse } from "axios";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import DocumentsFormAPIRequest, { BulkDocumentRequest } from "documents/_api/commands/documents-form-api-request";
import UpdateDocumentAPIRequest from "documents/_api/commands/update-document-api-request";
import DocumentQuery from "documents/_api/queries/document-query";
import DocumentSchema, { DocumentJSON } from "documents/_schema/document-schema";
import Document from "documents/document";
import UnavailableDocumentSchema, {
  UnavailableDocumentJSON,
} from "documents/unavailable-documents/_schema/unavailable-document-schema";
import UnavailableDocument from "documents/unavailable-documents/unavailable-document";
import { validateDocumentUploadRequests } from "documents/_api/document-upload-validation";
import z from "zod";

export type DocumentApiResult = Document | UnavailableDocument;

export type DocumentPermissionTarget = {
  id: string;
  name: string;
};

type DocumentApiResultJSON = DocumentJSON | UnavailableDocumentJSON;

type DocumentListArgs = {
  query: DocumentQuery;
  interfaceName?: string;
};

type DocumentIdArgs = {
  documentId: string;
};

type DocumentsByIdsArgs = {
  documentIds: string[];
};

const DocumentApiResultJSONZ = z.union([
  DocumentSchema.DocumentJSONZ,
  UnavailableDocumentSchema.UnavailableDocumentJSONZ,
]);

const documentListTag = { type: "Document" as const, id: "LIST" };

function deserializeDocumentApiResult(documentJSON: DocumentApiResultJSON): DocumentApiResult {
  if ("issue" in documentJSON && documentJSON.issue.trim().length > 0) {
    return UnavailableDocument.fromJSON(documentJSON);
  }

  return Document.fromJSON(documentJSON);
}

function documentTags(documents?: DocumentApiResult[]) {
  return documents
    ? [
        ...documents.map((document) => ({ type: "Document" as const, id: document.id.value })),
        documentListTag,
      ]
    : [documentListTag];
}

export const documentsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Get
    getDocuments: build.query<PaginatedResponse<Document>, DocumentListArgs>({
      query: ({ query }) => ({
        url: "documents",
        params: query.searchParams,
      }),
      rawResponseSchema: z.array(DocumentSchema.DocumentJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: DocumentJSON[], meta): PaginatedResponse<Document> => {
        const paginationHeader = meta?.response?.headers.get("x-pagination") ?? "";
        return PaginatedResponse.from(Document.fromJSONArray(response), paginationHeader);
      },
      providesTags: (result) => documentTags(result?.data),
    }),

    getDocumentById: build.query<DocumentApiResult, DocumentIdArgs>({
      query: ({ documentId }) => `documents/${documentId}`,
      rawResponseSchema: DocumentApiResultJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: DocumentApiResultJSON): DocumentApiResult => deserializeDocumentApiResult(response),
      providesTags: (_result, _error, { documentId }) => [{ type: "Document" as const, id: documentId }],
    }),

    getDocumentsByIds: build.query<DocumentApiResult[], DocumentsByIdsArgs>({
      query: ({ documentIds }) => ({
        url: "documents/get-documents-info-by-ids",
        method: "POST",
        body: documentIds,
      }),
      rawResponseSchema: z.array(DocumentApiResultJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: DocumentApiResultJSON[]): DocumentApiResult[] =>
        response.map(deserializeDocumentApiResult),
      providesTags: documentTags,
    }),

    getDocumentPermissionTargetsByIds: build.query<DocumentPermissionTarget[], DocumentsByIdsArgs>({
      query: ({ documentIds }) => ({
        url: "documents/get-permission-targets-by-ids",
        method: "POST",
        body: documentIds,
      }),
      rawResponseSchema: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) =>
        result
          ? result.map((document) => ({ type: "Document" as const, id: document.id }))
          : [documentListTag],
    }),

    downloadDocument: build.query<AxiosResponse<Blob>, DocumentIdArgs>({
      query: ({ documentId }) => ({
        url: `documents/${documentId}/download`,
        responseHandler: async (response: Response) => {
          const headers: Record<string, string> = {};
          response.headers.forEach((value, key) => {
            headers[key] = value;
          });

          return {
            data: await response.blob(),
            headers,
            request: {
              getResponseHeader: (name: string) => response.headers.get(name),
            },
          } as AxiosResponse<Blob>;
        },
      }),
      providesTags: (_result, _error, { documentId }) => [{ type: "Document" as const, id: documentId }],
    }),
    // #endregion

    // #region Mutations
    createDocuments: build.mutation<DocumentApiResult[], { requests: BulkDocumentRequest[] }>({
      async queryFn({ requests }, _api, _extraOptions, fetchWithBQ) {
        const validation = validateDocumentUploadRequests(requests);
        const failuresByRequestId = new Map(
          validation.failures.map(({ request, code, issue }) => [
            request.requestId.value,
            UnavailableDocument.create({
              id: request.requestId,
              status: "refused",
              code,
              issue,
            }),
          ]),
        );

        if (validation.acceptedRequests.length === 0) {
          return {
            data: requests.map((request) => failuresByRequestId.get(request.requestId.value)!),
          };
        }

        const result = await fetchWithBQ({
          url: "documents",
          method: "POST",
          body: new DocumentsFormAPIRequest(validation.acceptedRequests).formData,
        });
        if (result.error) return { error: result.error };

        const serverResults = z.array(DocumentApiResultJSONZ)
          .parse(result.data)
          .map(deserializeDocumentApiResult);
        let serverResultIndex = 0;
        return {
          data: requests.map((request) =>
            failuresByRequestId.get(request.requestId.value) ?? serverResults[serverResultIndex++]
          ),
        };
      },
      invalidatesTags: (result) => {
        const uploadedDocuments = result?.filter((item): item is Document => item instanceof Document) ?? [];
        return uploadedDocuments.length > 0 ? documentTags(uploadedDocuments) : [];
      },
    }),

    updateDocument: build.mutation<
      DocumentApiResult,
      { originalDocument: Document; updatedDocument: Document }
    >({
      async queryFn({ originalDocument, updatedDocument }, _api, _extraOptions, fetchWithBQ) {
        if (!originalDocument.id || !updatedDocument.id) {
          throw new Error("Cannot update document without id.");
        }

        const request = new UpdateDocumentAPIRequest(originalDocument, updatedDocument);
        if (request.payload.length === 0) {
          return { data: updatedDocument };
        }

        const result = await fetchWithBQ({
          url: `documents/${originalDocument.id.value}`,
          method: "PATCH",
          body: request.payload,
        });
        if (result.error) return { error: result.error };

        return {
          data: deserializeDocumentApiResult(DocumentApiResultJSONZ.parse(result.data)),
        };
      },
      invalidatesTags: (_result, _error, { originalDocument }) => [
        { type: "Document" as const, id: originalDocument.id.value },
        documentListTag,
      ],
    }),

    deleteDocument: build.mutation<void, DocumentIdArgs>({
      query: ({ documentId }) => ({
        url: `documents/${documentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, error, { documentId }) => error ? [] : [
        { type: "Document" as const, id: documentId },
        documentListTag,
      ],
    }),

    archiveDocument: build.mutation<void, DocumentIdArgs>({
      query: ({ documentId }) => ({
        url: `documents/${documentId}/archive`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { documentId }) => [
        { type: "Document" as const, id: documentId },
        documentListTag,
      ],
    }),

    unarchiveDocument: build.mutation<void, DocumentIdArgs>({
      query: ({ documentId }) => ({
        url: `documents/${documentId}/unarchive`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { documentId }) => [
        { type: "Document" as const, id: documentId },
        documentListTag,
      ],
    }),
    // #endregion
  }),
});

export const {
  useGetDocumentsQuery,
  useGetDocumentByIdQuery,
  useGetDocumentsByIdsQuery,
  useGetDocumentPermissionTargetsByIdsQuery,
  useLazyDownloadDocumentQuery,
  useCreateDocumentsMutation,
  useUpdateDocumentMutation,
  useDeleteDocumentMutation,
  useArchiveDocumentMutation,
  useUnarchiveDocumentMutation,
} = documentsApi;
